// NeedCard — the ONE compact job-posting card, used by the employer list
// (Rekrytering) and the candidate job board. Same normalized NeedViewData as
// NeedView, so the two stay visually in sync. The leading slot shows the org
// logo (or a name-derived colored placeholder — #40), giving each posting a
// clear visual identity.

import React from 'react';
import { MapPin, Clock, Briefcase, CalendarClock } from 'lucide-react';
import { EntityAvatar } from '@/components/custom-ui/EntityAvatar';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';
import { formatHoursRange, formatWorkLocation, formatShortDate } from '@/lib/models/need';
import type { NeedViewData } from './NeedView';

interface NeedCardProps {
    data: NeedViewData;
    badge?: React.ReactNode;   // status (employer) / applied-status (candidate)
    meta?: React.ReactNode;    // extra trailing chip (e.g. prereq lock count)
    onClick?: () => void;
}

// One meta item (icon + text) in the stats row.
const Meta: React.FC<{ icon: React.ComponentType<any>; children: React.ReactNode }> = ({ icon: Icon, children }) => (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Icon className="w-3.5 h-3.5 text-gray-400" /> {children}
    </span>
);

const NeedCard: React.FC<NeedCardProps> = ({ data, badge, meta, onClick }) => {
    const subtitle = [data.unitLabel, data.organisationName].filter(Boolean).join(' · ');
    const shown = data.competencies.slice(0, 3);
    const extra = data.competencies.length - shown.length;

    return (
        <button
            type="button"
            onClick={onClick}
            className={`${NEXILINK_CARD_WHITE_ELEVATED} group w-full text-left p-5 transition-all hover:border-[#1A4D4F]/40 hover:shadow-md`}
        >
            <div className="flex items-start gap-4">
                <EntityAvatar icon={Briefcase} size="lg" orgName={data.organisationName || data.unitLabel} orgLogoUrl={data.organisationLogoUrl} />

                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-[15px] text-gray-900 leading-snug truncate group-hover:text-[#1A4D4F] transition-colors">
                            {data.role || 'Namnlös roll'}
                        </p>
                        {badge}
                    </div>
                    {subtitle && (
                        <p className="text-xs font-medium text-gray-500 truncate mt-0.5">{subtitle}</p>
                    )}

                    {/* Meta row */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3">
                        <Meta icon={MapPin}>{data.city || '—'}</Meta>
                        <Meta icon={Clock}>{formatHoursRange(data.minHours, data.maxHours)}</Meta>
                        <Meta icon={Briefcase}>{formatWorkLocation(data.workLocationOnSite, data.workLocationRemote)}</Meta>
                        {data.applicationDeadline && (
                            <Meta icon={CalendarClock}>Sista {formatShortDate(data.applicationDeadline)}</Meta>
                        )}
                    </div>

                    {(shown.length > 0 || meta) && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-gray-50">
                            {shown.map((c, i) => (
                                <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-[#A4C3B2]/30 text-[#1A4D4F] font-medium">{c}</span>
                            ))}
                            {extra > 0 && <span className="text-[11px] text-gray-400">+{extra}</span>}
                            {meta && <span className="ml-auto">{meta}</span>}
                        </div>
                    )}
                </div>
            </div>
        </button>
    );
};

export default NeedCard;
